import ArchiveControlRoutes from '@account/components/archiveControl/ArchiveControlRoutes.jsx';
import ErrorPage from '@components/common/ErrorPage';
import Loading from '@components/common/Loading';
import React, {Suspense, lazy} from 'react';
import SecureRoute from './SecureRoute';
import {ORDERS_URL} from '@account/config';
import {Redirect, Route, Switch, useLocation} from 'react-router-dom';
import {Routes} from '@account/utils/route';
import {hasRoleLevel, roleLevels} from '@account/utils';
import {useAuth} from '@apps-common/auth-react';
import {useFlags} from '@hooks/useFlags';
import {useUIMode} from '@hooks/useUIMode';

const AccountsPage = lazy(() => import('@components/accounts/AccountsPage'));
const NewAccountsPage = lazy(
  () => import('@components/accounts/NewAccountsPage')
);
const AccountDetailPage = lazy(
  () => import('@components/accounts/AccountDetailPage')
);
const NewAccountDetailPage = lazy(
  () => import('@components/accounts/NewAccountDetailPage')
);
const NewPlanDetailPage = lazy(
  () => import('@components/plans/NewPlanDetailPage')
);
const AllocationsPage = lazy(
  () => import('@components/accounts/AllocationsPage')
);
const WorkspaceDetailPage = lazy(
  () => import('@components/accounts/WorkspaceDetailPage')
);
const AdminReportsPage = lazy(
  () => import('@components/admin-reports/AdminReportsPage')
);
const SelfServiceOrdersPage = lazy(
  () => import('@components/self-service-orders/SelfServiceOrdersPage')
);
const ProvisioningInboxPage = lazy(
  () =>
    import('@components/provisioning/provisioning-overview/ProvisioningInboxPage')
);
const AllContractsPage = lazy(
  () => import('@components/provisioning/all-contracts/AllContractsPage')
);
const ContractDetailPage = lazy(
  () => import('@components/provisioning/contract-detail/ContractDetailPage')
);
const ContractValidationPage = lazy(
  () =>
    import('@components/provisioning/contract-validation/ContractValidationPage')
);
const ProvisioningOverviewPage = lazy(
  () =>
    import('@components/provisioning/provisioning-overview/ProvisioningOverviewPage')
);
const CreateCustomProvisionPage = lazy(
  () =>
    import('@components/provisioning/custom-provision/CreateCustomProvisionPage')
);
const LabsPage = lazy(() => import('@components/labs/LabsPage'));
const Organization = lazy(
  () => import('@components/organizations/Organization')
);
const OrgsPage = lazy(() => import('@components/organizations/OrgsPage'));
const PVContractDetailPage = lazy(
  () => import('@components/plans/PVContractDetailPage')
);
const PVLineItemPage = lazy(
  () => import('@components/common/pv-contracts/PVLineItemPage')
);
const PlanDetailPage = lazy(() => import('@components/plans/PlanDetailPage'));
const PlansPage = lazy(() => import('@components/plans/PlansPage'));
const ProductDetailPage = lazy(
  () => import('@components/products/ProductDetailPage')
);
const NewProductDetailPage = lazy(
  () => import('@components/products/NewProductDetailPage')
);
const SentinelHubRedirectPage = lazy(
  () => import('@components/app/sentinel-hub/SentinelHubRedirectPage')
);
const TaskingContractDetailPage = lazy(
  () => import('@components/tasking/TaskingContractDetailPage')
);
const UserDashboardPage = lazy(
  () => import('@components/users/UserDashboardPage')
);
const UserProfilePage = lazy(() => import('@components/users/UserProfilePage'));
const UsersTablePage = lazy(() => import('@components/users/UsersTablePage'));
const MigrationWizard = lazy(
  () => import('@components/migration/MigrationWizard')
);
const OrgSetupWizard = lazy(
  () => import('@components/migration/OrgSetupWizard')
);
const FeatureUnavailablePage = lazy(
  () => import('@components/migration/FeatureUnavailablePage')
);

const AppRouter = () => {
  const auth = useAuth();
  const {pathname} = useLocation();
  const {isNewUI} = useUIMode();
  const {
    deprecateContracts,
    deprecateContractsProvisioningOnly,
    showAllAccounts,
    showAllocationUI,
    migrationWizardAccess,
    manageArchiveControl,
  } = useFlags();

  const isOrgUser = hasRoleLevel(auth, [roleLevels.org_user]);
  const isOrgAdminOrPlaneteer = hasRoleLevel(auth, [
    roleLevels.org_admin,
    roleLevels.planet_user,
    roleLevels.planet_admin,
  ]);

  return (
    <Suspense fallback={<Loading />}>
      <Switch>
        {/* Remove trailing slashes */}
        <Redirect from="/:url*(/+)" to={pathname.slice(0, -1)} />

        {/* Planetary Variables redirects, set in place July 2023 */}
        <Redirect
          from="/planetary-variable-contracts/add-contract"
          to={Routes.CONTRACTS_ADD}
        />
        <Redirect
          from="/planetary-variable-contracts/:contractId/products/:lineItemId"
          to={Routes.CONTRACT_LINEITEM_DETAIL}
        />
        <Redirect
          from="/planetary-variable-contracts/:contractId"
          to={Routes.CONTRACT_DETAIL}
        />
        <Redirect from="/planetary-variable-contracts" to={Routes.CONTRACTS} />
        <Redirect
          from="/plans/planetary-variable-contract/:contractId/product/:lineItemId"
          to={Routes.PLAN_CONTRACT_LINEITEM_DETAIL}
        />
        <Redirect
          from="/plans/planetary-variable-contract/:contractId"
          to={Routes.PLAN_CONTRACT_DETAIL}
        />

        {/* Accounts */}

        {showAllAccounts && showAllocationUI && (
          <SecureRoute
            path={Routes.ACCOUNT_ALLOCATIONS}
            component={AllocationsPage}
            roles={[roleLevels.planet_admin]}
            exact
          />
        )}

        {showAllAccounts && isNewUI && (
          <SecureRoute
            path={Routes.ACCOUNT_PLAN_DETAIL}
            component={NewPlanDetailPage}
            roles={[roleLevels.planet_admin]}
            exact
          />
        )}

        {showAllAccounts && (
          <SecureRoute
            path={Routes.WORKSPACE_DETAIL}
            component={WorkspaceDetailPage}
            roles={[roleLevels.planet_admin]}
            exact
          />
        )}

        {showAllAccounts && (
          <SecureRoute
            path={Routes.ACCOUNT_DETAIL}
            component={isNewUI ? NewAccountDetailPage : AccountDetailPage}
            roles={[roleLevels.planet_admin]}
            exact
          />
        )}

        {showAllAccounts && (
          <SecureRoute
            path={Routes.ACCOUNTS}
            component={isNewUI ? NewAccountsPage : AccountsPage}
            roles={[roleLevels.planet_admin]}
          />
        )}

        {/* Contracts */}

        {!deprecateContracts && (
          <SecureRoute
            path={Routes.CONTRACTS_ADD}
            component={CreateCustomProvisionPage}
            roles={[roleLevels.planet_admin]}
            exact
          />
        )}

        {!deprecateContracts && (
          <SecureRoute
            path={Routes.CONTRACTS_ALL}
            component={AllContractsPage}
            roles={[roleLevels.planet_admin]}
            exact
          />
        )}

        <SecureRoute
          path={Routes.CONTRACT_LINEITEM_DETAIL}
          component={PVLineItemPage}
          roles={[roleLevels.planet_admin]}
          exact
        />

        {deprecateContracts && (
          <Redirect
            exact
            from={Routes.CONTRACTS_ADD}
            to={Routes.PROVISION_ADD}
          />
        )}

        {!deprecateContractsProvisioningOnly && (
          <SecureRoute
            path={Routes.CONTRACT_VALIDATE}
            component={ContractValidationPage}
            roles={[roleLevels.planet_admin]}
            exact
          />
        )}

        {!deprecateContractsProvisioningOnly && (
          <SecureRoute
            path={Routes.CONTRACT_DETAIL}
            component={ContractDetailPage}
            roles={[roleLevels.planet_admin]}
            exact
          />
        )}

        {!deprecateContracts && (
          <SecureRoute
            path={Routes.CONTRACTS}
            component={ProvisioningOverviewPage}
            roles={[roleLevels.planet_admin]}
            exact
          />
        )}

        {/* Dashboard */}

        <SecureRoute
          path={Routes.DASHBOARD}
          exact
          component={UserDashboardPage}
          roles={[roleLevels.org_user]}
        />

        {/* Labs */}

        <SecureRoute
          path={Routes.LABS}
          component={LabsPage}
          roles={[roleLevels.planet_user, roleLevels.planet_admin]}
        />

        {/* Orders */}

        {/* Self-Service Orders — must precede ORDER_DETAIL (:orderId) to avoid shadowing */}
        <SecureRoute
          path={Routes.SELF_SERVICE_ORDERS}
          component={SelfServiceOrdersPage}
          roles={[roleLevels.planet_admin]}
        />

        <Route
          path={Routes.ORDER_DETAIL}
          exact
          render={({match}) => {
            window.location.replace(`${ORDERS_URL}${match.params.orderId}/`);
          }}
        />

        <Route
          path={Routes.ORDERS}
          exact
          render={() => {
            window.location.replace(ORDERS_URL);
          }}
        />

        {/* Organizations */}

        <SecureRoute
          path={Routes.ORG_DETAIL}
          component={Organization}
          roles={[
            roleLevels.org_admin,
            roleLevels.planet_user,
            roleLevels.planet_admin,
          ]}
        />

        <SecureRoute
          path={Routes.ORGS}
          component={OrgsPage}
          roles={[
            roleLevels.org_admin,
            roleLevels.planet_user,
            roleLevels.planet_admin,
          ]}
        />

        {/* Plans */}

        <Route path={Routes.PLAN_TASKING} exact>
          <Redirect to={`${Routes.PLANS}?tab=tasking`} />
        </Route>

        <Route path={Routes.PLAN_TASKING_DETAIL} exact>
          <TaskingContractDetailPage />
        </Route>

        <Route path={Routes.PLAN_CONTRACT_DETAIL} exact>
          <PVContractDetailPage />
        </Route>

        <Route path={Routes.PLAN_CONTRACT_LINEITEM_DETAIL} exact>
          <PVLineItemPage />
        </Route>

        <Route path={Routes.PLAN_CONTRACTS} exact>
          <Redirect to={`${Routes.PLANS}?tab=pv`} />
        </Route>

        <Route path={Routes.PLAN_DETAIL} exact>
          <PlanDetailPage />
        </Route>

        <Route path={Routes.PLANS} component={PlansPage} />

        {/* Products */}

        <SecureRoute
          path={Routes.PRODUCT_DETAIL}
          component={isNewUI ? NewProductDetailPage : ProductDetailPage}
          roles={[roleLevels.planet_admin]}
        />

        {/* Provision */}

        {deprecateContracts && (
          <SecureRoute
            path={Routes.PROVISION_ADD}
            component={CreateCustomProvisionPage}
            roles={[roleLevels.planet_admin]}
            exact
          />
        )}

        {deprecateContracts && (
          <SecureRoute
            path={Routes.PROVISION}
            component={
              isNewUI ? ProvisioningInboxPage : ProvisioningOverviewPage
            }
            roles={[roleLevels.planet_admin]}
            exact
          />
        )}

        {/* Users */}

        <SecureRoute
          path={Routes.USER_DETAIL}
          component={UserProfilePage}
          exact
          roles={[
            roleLevels.org_admin,
            roleLevels.planet_user,
            roleLevels.planet_admin,
          ]}
        />

        <SecureRoute
          path={Routes.USERS}
          component={UsersTablePage}
          exact
          roles={[
            roleLevels.org_admin,
            roleLevels.planet_user,
            roleLevels.planet_admin,
          ]}
        />

        <Route path={Routes.USER_SETTINGS} exact component={UserProfilePage} />

        {/* Migration Wizard */}

        <SecureRoute
          path={Routes.MIGRATION_EVAL_SETUP}
          component={
            migrationWizardAccess
              ? OrgSetupWizard
              : () => <FeatureUnavailablePage featureName="Migration Setup" />
          }
          exact
          roles={[roleLevels.planet_admin]}
        />

        <SecureRoute
          path={Routes.MIGRATION_WIZARD}
          component={
            migrationWizardAccess
              ? MigrationWizard
              : () => <FeatureUnavailablePage featureName="Migration Wizard" />
          }
          exact
          roles={[roleLevels.planet_admin]}
        />

        <Route
          path={Routes.SENTINEL_HUB_REDIRECT}
          exact
          component={SentinelHubRedirectPage}
        />

        {/* Admin Reports */}
        <SecureRoute
          path={Routes.ADMIN_REPORTS}
          component={AdminReportsPage}
          roles={[roleLevels.planet_admin]}
        />

        {/* Archive Control */}
        {manageArchiveControl && (
          <SecureRoute
            path={Routes.ARCHIVE_CONTROL}
            component={ArchiveControlRoutes}
            roles={[roleLevels.planet_admin]}
          />
        )}

        {/* Root */}

        <Route path={Routes.ROOT} exact>
          {!auth?.user?.claims?.role_level ? (
            <Loading />
          ) : isNewUI &&
            showAllAccounts &&
            hasRoleLevel(auth, [roleLevels.planet_admin]) ? (
            <Redirect to={Routes.ACCOUNTS} />
          ) : isOrgUser ? (
            <Redirect to={Routes.DASHBOARD} />
          ) : isOrgAdminOrPlaneteer ? (
            <Redirect to={Routes.ORGS} />
          ) : (
            <ErrorPage />
          )}
        </Route>

        {/*
          Catch-all. /accounts paths render a loading state instead of a 404 so
          there's no 404 flash while showAllAccounts resolves after the
          planet-admin root -> /accounts redirect above. The root redirect is
          gated on showAllAccounts, so an admin only lands here (on the spinner)
          while the flag is still resolving, never permanently.
        */}
        <Route path="*">
          {auth?.user?.claims?.role_level &&
          pathname !== Routes.ACCOUNTS &&
          !pathname.startsWith(Routes.ACCOUNTS + '/') ? (
            <ErrorPage statusCode={404} />
          ) : (
            <Loading />
          )}
        </Route>
      </Switch>
    </Suspense>
  );
};

export default AppRouter;
